import {
  EmployeeTargetProfile_EmployeeSkillFragment,
  EmployeeTargetProfile_PositionSkillAssociationFragment,
} from '../../../__generated__/graphql';
import { isSkillLevel, SkillLevel } from '../../../pages/Skills/common/types';

// How the employee's current level compares to what the position requires.
// `unknown` covers rows we cannot judge: no required level defined, or the
// employee has no rating for the skill.
export type TargetProfileGap = 'met' | 'close' | 'far' | 'unknown';

export type TargetProfileRow = {
  id: string;
  skillName: string;
  targetLevel: SkillLevel | undefined;
  currentLevel: SkillLevel | undefined;
  gap: TargetProfileGap;
};

// One level short still counts as close enough; two or more is a real gap.
const CLOSE_ENOUGH_SHORTFALL = 1;

export const getTargetProfileGap = (
  currentLevel: SkillLevel | undefined,
  targetLevel: SkillLevel | undefined,
): TargetProfileGap => {
  // `null` is a valid SkillLevel meaning "not rated", so it is not comparable
  // either — treat it like a missing level rather than a zero.
  if (targetLevel == null || currentLevel == null) {
    return 'unknown';
  }
  if (currentLevel >= targetLevel) {
    return 'met';
  }
  return targetLevel - currentLevel <= CLOSE_ENOUGH_SHORTFALL ? 'close' : 'far';
};

// Target levels come from Salesforce as a plain Int, so anything outside the
// 1-5 skill scale is mapped to `undefined` and rendered as "not defined"
// instead of being shown as a bogus level. `null` is excluded explicitly:
// `isSkillLevel` accepts it (an employee's rating can legitimately be "not
// rated"), but a requirement without a target level has none defined at all.
const toSkillLevel = (value: number | null): SkillLevel | undefined =>
  value !== null && isSkillLevel(value) ? value : undefined;

export const mapTargetProfileRows = (
  requirements: readonly EmployeeTargetProfile_PositionSkillAssociationFragment[],
  employeeSkills: readonly EmployeeTargetProfile_EmployeeSkillFragment[] = [],
): TargetProfileRow[] => {
  // The employee's own rating for a skill lives on a separate association, so
  // index it by skill id to pair each requirement with its current level.
  const currentLevelBySkillId = new Map(
    employeeSkills.map((employeeSkill) => [
      employeeSkill.skill.Id,
      toSkillLevel(employeeSkill.flair__Rate__c),
    ]),
  );

  return requirements.map((requirement) => {
    const targetLevel = toSkillLevel(requirement.flair__Rate__c);
    // Undefined covers both "skill not assigned" and "assigned but unrated";
    // both render as "not defined" rather than implying a level of 0.
    const currentLevel = currentLevelBySkillId.get(requirement.skill.Id);

    return {
      id: requirement.Id,
      skillName: requirement.skill.localizedName,
      targetLevel,
      currentLevel,
      gap: getTargetProfileGap(currentLevel, targetLevel),
    };
  });
};

// Highest target level first so the most demanding requirements lead, then by
// name for a stable order among equal levels.
export const sortTargetProfileRows = (
  rows: readonly TargetProfileRow[],
): TargetProfileRow[] =>
  [...rows].sort((a, b) => {
    const levelDiff = (b.targetLevel ?? 0) - (a.targetLevel ?? 0);
    return levelDiff !== 0 ? levelDiff : a.skillName.localeCompare(b.skillName);
  });
